// frontend/packages/business-console/src/components/content/PageTrafficPanel.jsx
//
// A compact "which pages are actually getting traffic and leads" rail at the
// top of the content sidebar, so the operator edits with demand in view
// instead of guessing. Reads the first-party page-insights endpoint
// (/v2/visitors/page-insights) - views from the rollups + leads-from-content
// from form submissions, joined per page. Read-only.
//
// There is no stored content-file -> public-route mapping (content lives in
// GitHub, routes are decided by the site template), so this ranks the real
// tracked page paths rather than pretending to match each editor file. It
// renders nothing when there's no traffic data yet, so it never shows an
// empty or broken box.

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import { useTokens, Surface } from '../primitives';
import { getPageInsights, getPageSeo } from '../../services/visitorsService';

const WINDOW_DAYS = 30;
const TOP_N = 6;

// SEO health band -> token color key.
const HEALTH_COLOR = { good: 'green', warn: 'amber', bad: 'red' };

export default function PageTrafficPanel({ businessId }) {
  const t = useTokens();
  const [items, setItems] = useState(null);
  const [seoByPath, setSeoByPath] = useState(() => new Map());
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!businessId) return undefined;
    let cancelled = false;
    // Views + leads (always available); SEO health is best-effort (only once
    // a UAVS scan has run), so a missing/failed page-seo never blanks the rail.
    Promise.all([
      getPageInsights(WINDOW_DAYS).catch(() => ({ pages: [] })),
      getPageSeo().catch(() => ({ pages: [] })),
    ]).then(([insights, seo]) => {
      if (cancelled) return;
      setItems(Array.isArray(insights?.pages) ? insights.pages : []);
      const map = new Map();
      for (const p of Array.isArray(seo?.pages) ? seo.pages : []) {
        if (p?.path) map.set(p.path, p);
      }
      setSeoByPath(map);
    });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  // Render nothing until we have at least one tracked page with traffic.
  if (!items || items.length === 0) return null;

  const top = items.slice(0, TOP_N);
  const maxViews = Math.max(...top.map((p) => p.views || 0), 1);

  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <Surface radius="12px" sx={{ p: 1.25 }}>
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
          }}
        >
          <InsightsOutlinedIcon sx={{ fontSize: 16, color: t.blue }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '.4px',
              color: t.faint,
              flexGrow: 1,
            }}
          >
            Page traffic &amp; leads &middot; {WINDOW_DAYS} days
          </Typography>
          <IconButton
            size="small"
            sx={{
              p: 0.25,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
            aria-label={open ? 'Collapse page traffic' : 'Expand page traffic'}
          >
            <ExpandMoreIcon sx={{ fontSize: 18, color: t.faint }} />
          </IconButton>
        </Box>

        <Collapse in={open} unmountOnExit>
          <Box
            sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.875 }}
          >
            {top.map((p) => {
              const pct = Math.round(((p.views || 0) / maxViews) * 100);
              const seo = seoByPath.get(p.path);
              return (
                <Box key={p.path}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: t.text,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={p.path}
                    >
                      {p.path || '/'}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexShrink: 0,
                      }}
                    >
                      {seo && typeof seo.onPageScore === 'number' ? (
                        <Box
                          component="span"
                          title={
                            seo.issues && seo.issues.length
                              ? `SEO: ${seo.issues.join(', ')}`
                              : 'SEO: no issues found'
                          }
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: 10,
                            fontWeight: 800,
                            color: t[HEALTH_COLOR[seo.health]] || t.faint,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: t[HEALTH_COLOR[seo.health]] || t.faint,
                            }}
                          />
                          SEO {seo.onPageScore}
                        </Box>
                      ) : null}
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: t.textSecondary,
                        }}
                      >
                        {(p.views || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 0.375,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: t.subtle,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: t.blue,
                      }}
                    />
                  </Box>
                  {p.leads > 0 ? (
                    <Typography
                      sx={{
                        mt: 0.375,
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: t.greenText,
                      }}
                    >
                      {p.leads} {p.leads === 1 ? 'lead' : 'leads'} from this
                      page
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Surface>
    </Box>
  );
}
